import React, { useCallback, useEffect, useMemo, useState } from 'react'
import GenericErrorModal from 'components/VirtualPortal/components/ErrorModal'
import GenericPopUpModal from 'components/VirtualPortal/components/PopUpModal'
import postSessionRegistration from 'components/VirtualPortal/helpers/postSessionRegistration'
import getLabel from 'utilities/label.js'
import handleTiming, { meetingHasEnded, meetingHasStarted } from 'components/VirtualPortal/helpers/handleTiming'
import buttonStatusRestrictions from 'components/VirtualPortal/helpers/buttonStatusRestrictions'
import RegisterButton from './RegisterButton'
import SessionIcons from './SessionIcons'
import SessionDescription from './SessionDescription'
import OneOnOneTimeslotRow from './OneOnOneTimeslotRow'
import EvaluationModal from './EvaluationModal'
import 'moment-timezone'
import _ from 'lodash'

const SessionRow = ({ 
  session, 
  needsRefresh, 
  oneOnOneWizardIsComplete, 
  userDetails, 
  flashMessage, 
  isEventDay,
  virtualPortalPreRegistrationClosed
}) => {
  const [ state, setState ] = useState({
    session,
    meetingEnded: meetingHasEnded(session),
    meetingHasStarted: meetingHasStarted(session),
    errorModalIsOpen: false,
    genericModalIsOpen: false,
    userDetails,
    oneOnOneWizardIsComplete,
    sessionVisible: session.session_type_key == 'session_type_one_to_one_meeting',
    evaluationCompleted: session.evaluation_completed
  })

  const sessionOver = useMemo(() => state.meetingEnded ? 'session-over' : '',[state])
  const notAuthorized = useMemo(() => state.session.registration_status === 'not_authorized' ? 'not-authorized' : '',[state])
  const registeredSession = useMemo(() => state.session.registration_status === 'un_register' && !state.session.evaluation_completed ? 'registered' : '', [state])
  const p2pSession = useMemo(() => state.session.session_type_key == 'session_type_one_to_one_meeting' ? 'p2pSession' : '', [state])
  const disabled = useMemo(() => state.session.registration_status === 'session_full'  || state.session.session_type_key == 'session_type_one_to_one_meeting' ? 'disabled' : '',[state])

  const setNewState = useCallback((data) => {
    setState({ ...state, ...data })
  },[state])

  useEffect(() => {
    const session = state.session
    handleTiming(session, setNewState)
  },[state])

  const refresh = useCallback(() => {
    window.api.post(`/virtual_portal/toggle_registration/${state.session.id}`).then((response) => {
      let session = state.session
      if(['double_booked', 'session_full'].includes(response.data.event_session_registration.registration_status)){
        return false
      }
      session.registration_status = response.data.event_session_registration.registration_status
      session.event_session_registration_virtual_platform_url = response.data.event_session_registration.event_session_registration_virtual_platform_url
      setNewState({
        session
      })
    })
  },[state])

  useEffect(() => {
    if (window.location.href.includes('#session-')) {
      let id = window.location.href.split('#').pop()
      let el = document.getElementById(id)
      el.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
    if ((needsRefresh || state.session.registration_status === 'auto_register') && state.session.session_type_key != 'session_type_one_to_one_meeting'){
      refresh()
    }
    if (window.location.href.includes('#peer_to_peer_meeting_session')) {
      let el = document.getElementById('p2p')
      if(el) {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    }
  },[needsRefresh])

  const registerableClass = buttonStatusRestrictions(state) ? 'hide' : ''

  const toggleRegistrationStatus = useCallback(() => {
    if (state.session.registration_status === 'not_authorized' || state.session.registration_status === 'session_full'){
      return
    }
    if (state.session.registration_status === 'un_register' && state.meetingEnded) {
      return
    }

    postSessionRegistration({ key: 'session_registration', data: { id: state.session.id }, callback: (response) => {
      if(response.data.event_session_registration.registration_status === 'double_booked') {
        flashMessage({
          message: getLabel('schedule_conflict'),
          type: 'error'
        })
        return false
      }
    }}, setNewState, state)
  },[state, flashMessage, postSessionRegistration, setNewState])

  const getOneOnOneTimeslots = () => {
    if(state.userDetails.status === 'registration_status_approved' && state.userDetails.p2p_opt_in){
      let oneOnOneTimeslots = []
      let timeslots = _.sortBy(state.session.event_session_one_on_one_timeslots, 'epoch_start_time')
      _.each(timeslots, function (oneOnOneTimeslot) {
        oneOnOneTimeslots.push(<OneOnOneTimeslotRow oneOnOneTimeslot={oneOnOneTimeslot} key={oneOnOneTimeslot.id} />)
      })
      return oneOnOneTimeslots
    }
  }

  const toggleModal = useCallback((openChat, data) => {
    setNewState({
      ...state,
      ...data,
      showDoubleBookedModal: false,
      showSessionFullModal: false,
      errorModalIsOpen: false,
      genericModalIsOpen: false,
      isOpen: false
    })
    if(openChat && window.LiveChatWidget){
      window.expandLiveChat()
    }
  },[state, setNewState])
  
  return(
    <div key={state.session.id} >
      <div className='full-session-wrapper'>
        <div className={'row session ' + sessionOver}  id={'session-' + state.session.id} >
          <div className={'session-cta-wrapper ' + notAuthorized } style ={ { backgroundImage: 'url('+session.imageUrl+')' } }>
            <button 
              className={`registration-button ${registeredSession} ${registerableClass} ${p2pSession}`} 
              onClick={toggleRegistrationStatus} 
              disabled={disabled}
            >
              <RegisterButton state={state} />
            </button>
          </div>
          <SessionDescription 
            state={state} 
            setState={setNewState}
          />
          <SessionIcons 
            isEventDay={isEventDay} 
            state={state} 
            setState={setNewState} 
            virtualPortalPreRegistrationClosed={virtualPortalPreRegistrationClosed}
          />
        </div>
        {session.event_session_one_on_one_timeslots ? getOneOnOneTimeslots() : ''}
      </div>
      <EvaluationModal
        toggleModal={toggleModal}
        isOpen={state.isOpen}
        session={session}
        sessionType='session'
        setNewState={setNewState}
      />
      <GenericErrorModal 
        isOpen={state.showDoubleBookedModal} 
        toggleModal={toggleModal}
        withOpen
      >
        <h5>Your agenda is full for this time slot. Please remove one session to continue. </h5>
        <h6>How to change a session selection:</h6>
        <ol>
          <li>Click the ‘-‘ button, located on the left side of the session tile, to remove the session from your schedule.</li>
          <li>Click the ‘+‘ button to select the preferred session to add to your schedule.</li>
          <li>Select ‘My Schedule‘ to view only your selected sessions.</li>
        </ol>
        <hr></hr>
        <h6>Need additional help?</h6>
      </GenericErrorModal>
      <GenericErrorModal isOpen={state.showSessionFullModal} toggleModal={toggleModal}>
        <div className='generic-error-modal-header'>
          <i className='fa fa-times-thin fa-2x' onClick={toggleModal}></i>
        </div>
        <h4>This Session Has Filled Up</h4>
        <hr></hr>
        <p>It looks like the last available seat has been taken, please select another session in this timeblock.</p>
      </GenericErrorModal>
      <GenericErrorModal
        toggleModal={toggleModal}
        isOpen={state.errorModalIsOpen}
      >
        <h5>{state?.errorStatus}</h5>
        <hr/>
        <h6>{state?.errorMessage}</h6>
      </GenericErrorModal>
      {state.genericModalIsOpen &&
        <GenericPopUpModal
          customClass={state.genericModalCustomClass}
          handleCloseGenericModal={toggleModal}
          content={state.genericModalContent}
          modalCalendarLink={state.modalCalendarLink}
          customCancelButton={state.genericModalCustomCancelButton} 
        />
      }
    </div>
  )

}

export default SessionRow